/**
 * ArloSEO Help / Resource docs registry.
 *
 * v1 = static markdown shipped with the frontend so the docs are crawlable,
 * fast, and don't depend on any backend. Each article is keyed by `slug`
 * and grouped by `category`. To add a new article, append an entry below.
 */

export const DOC_CATEGORIES = [
  { id: "start", label: "Get started" },
  { id: "core", label: "Core workflows" },
  { id: "publish", label: "Publishing" },
  { id: "account", label: "Account & billing" },
];

export const DOCS = [
  {
    slug: "getting-started",
    category: "start",
    title: "Getting started with ArloSEO",
    excerpt:
      "Create your first project, point Arlo at your domain, and ship your first audit in under five minutes.",
    reading_minutes: 4,
    updated: "2026-02-10",
    videoUrl: null,
    videoTitle: "Getting started with ArloSEO · 90-second walkthrough",
    body: `# Getting started with ArloSEO

Welcome — Arlo's glad you're here. This guide walks you through the first 5 minutes inside ArloSEO so you can ship your first audit, content draft, and tracked keyword today.

## 1. Create your account

Head to [the signup page](/register) and create a free account with your work email. You'll start on the **14‑day Pro trial** automatically — no card required.

> Already have an account? [Sign in](/login) and skip to step 2.

## 2. Add your first project

A *project* in ArloSEO maps 1:1 to a domain you want Arlo to track.

1. Open the **dashboard** at \`/app\`.
2. Click **New project**.
3. Enter the project name (e.g. *"Acme Marketing Site"*) and root domain (e.g. \`acme.com\`).
4. Hit **Create**.

Arlo will queue your first audit automatically. You'll see a score appear on the dashboard within a few seconds.

## 3. Run your first audit

The audit runs against the root URL by default. To audit a specific page, open the project and use the **Audit URL** input at the top.

Each audit returns:

- An overall **0–100 score**
- A breakdown across **performance, on‑page SEO, accessibility, and best‑practices**
- A list of **actionable fixes** ranked by impact

Read the full guide → [Run your first audit](/docs/run-your-first-audit)

## 4. Track keywords

Open any project and switch to the **Keywords** tab. Add 5–10 seed keywords you care about. Arlo will pull search volume, difficulty, and your current rank position (mocked in trial mode — connect SerpApi to unlock live data).

## 5. Draft your first post with Arlo

Switch to the **Content Studio** tab in any project, hit **New draft**, give Arlo a target keyword and intent, and watch a full SEO‑optimized brief + draft appear in seconds.

When you're happy with it, publish directly to **WordPress**, copy as Markdown into **Notion**, or post it to the internal ArloSEO **/blog** with a single click.

## What's next?

- 📈 [Track keywords & rankings](/docs/track-keywords)
- ✍️ [Use the Content Studio](/docs/content-studio)
- 🚀 [Publish to WordPress or Notion](/docs/publish-to-wordpress)
- 💳 [Billing, plans & trials](/docs/billing-and-plans)

Need a hand? Email **support@arloseo.com** — a human (not Arlo) will reply within one business day.
`,
  },
  {
    slug: "run-your-first-audit",
    category: "core",
    title: "Run your first SEO audit",
    excerpt:
      "Score any URL on performance, on‑page SEO, accessibility, and best‑practices — then turn the findings into a fix list.",
    reading_minutes: 5,
    updated: "2026-02-10",
    videoUrl: null,
    videoTitle: "Running your first audit — what every section means",
    body: `# Run your first SEO audit

An audit in ArloSEO is a snapshot of how a single URL is performing across **four pillars**: performance, SEO, accessibility, and best‑practices. Audits are non‑destructive and free across all plans — run them as often as you like.

## Two ways to audit

### 1. Free public audit (no signup)

Drop any URL into the [/free-audit](/free-audit) tool to get a one‑shot score with no account required. Great for sharing with a client or sanity‑checking a competitor.

### 2. Project audit (recommended)

Audits run from inside a project get tracked over time, so you can see your score climb (or fall) week over week.

1. Open the project from your [dashboard](/app).
2. Paste a URL into the **Audit URL** input at the top.
3. Click **Run audit**.

Arlo will fetch the page, render it, and score it. Results stream in within ~5–10 seconds.

## Understanding the score

The headline number is a weighted blend of the four pillar scores:

| Pillar | Weight | What it measures |
| --- | --- | --- |
| Performance | 35% | Core Web Vitals, render time, JS payload |
| SEO | 35% | On‑page tags, headings, structured data, meta |
| Accessibility | 15% | Alt text, contrast, ARIA, semantic HTML |
| Best practices | 15% | HTTPS, console errors, deprecated APIs |

Anything **≥ 90** is "shipping shape." Below **70** and Arlo will start nudging you in the dashboard.

## The fix list

Below the score, you'll see a ranked list of issues. Each issue includes:

- A short, plain‑English description
- The **impact** (high / medium / low)
- Where Arlo found it (selector + snippet)
- A suggested fix you can copy/paste

Click any issue to expand the technical detail.

## Tracking progress

Every audit is saved to the project's audit history. The **Sparkline** at the top of each project shows your last 30 audits at a glance — so you can ship a fix, re‑audit, and watch the line go up.

> **Pro tip:** schedule a weekly digest from the project settings. Arlo will email you every Monday with your score delta, top wins, and the most painful regression.

## Common questions

**Does ArloSEO crawl my whole site?** Not in v1 — audits are per‑URL. Site‑wide crawls are on the roadmap.

**Will an audit affect my analytics?** Arlo identifies itself with a clearly‑named user agent. You can filter it out in GA / Plausible if needed.

**My score dropped — what do I do?** Open the latest audit, sort the fix list by impact, and ship the top item. Then re‑audit. Repeat.
`,
  },
  {
    slug: "track-keywords",
    category: "core",
    title: "Track keywords & rankings",
    excerpt:
      "Seed Arlo with the queries you care about and watch volume, difficulty, and rank position evolve over time.",
    reading_minutes: 4,
    updated: "2026-02-10",
    videoUrl: null,
    videoTitle: "Adding your first 10 keywords (and how to pick them)",
    body: `# Track keywords & rankings

Keyword tracking is where ArloSEO earns its keep. Tell Arlo which queries matter, and he'll pull search volume, keyword difficulty, and your current position once a day.

## Add your first keywords

1. Open a project and switch to the **Keywords** tab.
2. Click **Add keyword** and paste in a comma‑separated list (or one per line).
3. Hit **Track**.

You can track up to **50 keywords** on the free trial and unlimited on Pro.

## What you'll see

Each tracked keyword shows:

- **Search volume** — monthly searches in your target country
- **Difficulty** — 0–100, higher = harder to rank
- **Your position** — best ranking URL on your domain
- **Trend** — 30‑day mini sparkline so you can spot movement at a glance

> Keyword and SERP data is **mocked** during the trial. Connect a SerpApi or DataForSEO key from your account settings to switch to live data. (Reach out if you need help wiring this up.)

## Grouping & filtering

Add tags to keywords (\`tag: pricing\`, \`tag: blog\`) to slice them by funnel stage, topic cluster, or campaign. Tags become filterable chips at the top of the Keywords tab.

## Finding new keywords with Arlo

In the Content Studio, ask Arlo to *"suggest 20 long‑tail keywords for [topic]"* and he'll pull a list of low‑difficulty, high‑intent queries you can add to tracking in one click.

## Best practices

1. **Start narrow.** 5–10 keywords you actually care about beats 500 you'll never look at.
2. **Mix intent.** Track a balance of bottom‑funnel ("buy") and top‑funnel ("what is") queries.
3. **Tag everything.** It pays off in month two when you have 50+ tracked terms.
4. **Pair with audits.** When a keyword drops, re‑audit the landing page first — usually it's a performance regression, not a content problem.
`,
  },
  {
    slug: "content-studio",
    category: "core",
    title: "Using the Content Studio (with Arlo AI)",
    excerpt:
      "Brief, draft, and edit SEO‑optimized posts inside the rich editor — with Arlo helping you rewrite, tighten, and rank.",
    reading_minutes: 6,
    updated: "2026-02-10",
    videoUrl: null,
    videoTitle: "From blank page to published post in 4 minutes",
    body: `# Using the Content Studio

The **Content Studio** is the writing room inside ArloSEO. It pairs a TipTap rich‑text editor with Arlo (our AI) so you can go from blank page → SEO‑ready draft in one session.

## Anatomy of a draft

Every draft has four panels:

1. **Brief** — target keyword, intent, audience, and outline
2. **Editor** — the rich‑text canvas where the body lives
3. **SEO checks** — live score for title, meta, H1, internal links
4. **Publish** — one‑click export to WordPress, Notion, or the internal blog

## Step 1 — Brief Arlo

Hit **New draft**, then fill in:

- **Target keyword** (e.g. "best seo tools for solo founders")
- **Intent** (informational / commercial / navigational)
- **Angle** (one sentence describing your unique POV)

Arlo will generate a working title, meta description, H2/H3 outline, and a complete first draft. You can regenerate any section independently.

## Step 2 — Edit in the rich editor

The editor is full TipTap rich text — **bold**, *italic*, links, headings, lists, code blocks, blockquotes, and inline images all work.

### Inline AI rewrite

Highlight any sentence or paragraph and click the **✨ Rewrite** button (or press ⌘/Ctrl + K). Pick a tone:

- **Tighten** — same meaning, fewer words
- **Friendlier** — softens jargon
- **More expert** — adds depth, precision
- **Add example** — appends a concrete example

The rewrite replaces the selection inline. Don't love it? Undo (⌘/Ctrl + Z) and try again.

## Step 3 — Watch your SEO score

The right‑hand panel updates as you type:

- Title length (50–60 chars ideal)
- Meta description (140–160 chars)
- H1 present + matches keyword
- Keyword density (0.5–2% sweet spot)
- Internal links count
- Reading time

Greens are good. Anything red, Arlo will explain in plain English.

## Step 4 — Publish

When you're happy, head to the **Publish** dropdown:

- **WordPress** — push directly via Application Password ([setup guide](/docs/publish-to-wordpress))
- **Notion** — copy as Markdown, paste into any Notion page
- **ArloSEO blog** — make it live at \`/blog/your-slug\` in one click
- **Markdown file** — download for any other CMS

Every publish lands in your **Publish History** on the dashboard (with CSV export).

## Tips from Arlo

- **Don't over‑optimize.** If the keyword density sneaks past 3%, the post starts to read robotic. Arlo will warn you.
- **Link to two existing posts.** Every new draft should link to at least two older posts on the same domain — it's the cheapest ranking win you'll ever ship.
- **Reuse outlines.** Found a brief that converts? Duplicate the draft and swap the keyword.
`,
  },
  {
    slug: "publish-to-wordpress",
    category: "publish",
    title: "Publish to WordPress or Notion",
    excerpt:
      "Connect your WordPress site once, then ship drafts from the Content Studio in one click. Notion exports work zero‑config.",
    reading_minutes: 5,
    updated: "2026-02-10",
    videoUrl: null,
    videoTitle: "Connecting WordPress with Application Passwords",
    body: `# Publish to WordPress or Notion

ArloSEO publishes finished drafts to the places you actually write — no copy‑paste, no formatting cleanup. Here's how to wire each one up.

## WordPress (one‑click publish)

ArloSEO uses **WordPress Application Passwords** — the official, recommended way for third‑party apps to publish. It's available on any self‑hosted WordPress 5.6+ install (and most managed hosts).

### Step 1 — Generate an Application Password

1. Log into your WordPress admin.
2. Go to **Users → Profile**.
3. Scroll to **Application Passwords**.
4. Name it \`ArloSEO\` and click **Add New**.
5. Copy the password WordPress shows you (it's only displayed once).

### Step 2 — Connect inside ArloSEO

1. In ArloSEO, open the project and go to **Settings → Integrations**.
2. Click **Connect WordPress**.
3. Paste your **site URL**, **username**, and the **application password** from step 1.
4. Hit **Save** — Arlo will verify the connection and pull your default category list.

### Step 3 — Publish a draft

From any draft in the Content Studio:

1. Click **Publish → WordPress**.
2. Pick the category, tags, and status (\`draft\` or \`publish\`).
3. Confirm.

The post lands in WordPress with your title, meta description (as Yoast/Rank Math compatible meta), featured image (if set), and full rich content. The publish gets logged in your dashboard's **Publish History**.

### Common WordPress issues

- **"401 Unauthorized"** — application passwords disabled. Check that **REST API** isn't blocked by a security plugin.
- **"403 Forbidden"** — the user account doesn't have \`publish_posts\` capability. Use an editor or administrator account.
- **HTML looks weird** — your theme is probably stripping classes. ArloSEO uses block‑editor‑safe HTML by default; toggle "Classic editor mode" in integration settings if you're on a legacy theme.

## Notion (copy‑paste, zero config)

Notion doesn't have a stable public API for arbitrary page injection, so Arlo gives you the **next best thing**: clean, pasteable Markdown.

1. From any draft, click **Publish → Copy to Notion**.
2. Arlo copies the entire post (title, meta, body, images‑as‑markdown) to your clipboard.
3. Paste into any Notion page (⌘/Ctrl + V).

Notion will auto‑convert the Markdown into native blocks. Headings, lists, code, links, and images all preserve.

## Internal ArloSEO Blog

Want to publish on \`/blog/your-slug\` instead? Hit **Publish → ArloSEO blog**. The post is live immediately and indexed by search engines via your sitemap.

## Publish History

Every successful publish — WordPress, Notion, internal blog, or Markdown file — gets logged on your dashboard's **Publish History** feed. You can:

- Filter by destination (WordPress / Notion / blog / file)
- Filter by status (success / failed)
- Export the whole feed as CSV (great for client reporting)

## What's next

- 🐢 [Use the Content Studio](/docs/content-studio)
- 💳 [Billing & plans](/docs/billing-and-plans)
`,
  },
  {
    slug: "billing-and-plans",
    category: "account",
    title: "Billing, plans & trials",
    excerpt:
      "How the 14‑day Pro trial works, what each plan includes, and how to manage your subscription, invoices, and refunds.",
    reading_minutes: 4,
    updated: "2026-02-10",
    videoUrl: null,
    videoTitle: "Trial, plans, and how billing works",
    body: `# Billing, plans & trials

Short version: every new account starts on a **14‑day free Pro trial** with no card required. After 14 days you stay on the free plan automatically unless you upgrade.

## The 14‑day Pro trial

When you sign up, your account is flagged \`on_trial = true\` for 14 days. During the trial you get:

- ✅ Unlimited audits
- ✅ Up to 50 tracked keywords
- ✅ Full Content Studio (Arlo AI included)
- ✅ All publish destinations (WordPress, Notion, internal blog)
- ✅ Weekly digests + Slack alerts

No card is captured. When the trial ends, your account silently moves to the **Free** plan — no surprise charges.

## Plans

| Plan | Audits / mo | Keywords | AI drafts | Publish | Support |
| --- | --- | --- | --- | --- | --- |
| **Free** | 20 | 5 | 3 | Markdown only | Community |
| **Pro** | Unlimited | 50 | Unlimited | All destinations | Email, 1 business day |
| **Studio** | Unlimited | 250 | Unlimited | All + team | Priority, same‑day |

See the live [pricing page](/pricing) for current monthly + annual rates.

## Upgrading

1. Open **Settings → Billing**.
2. Pick a plan and hit **Upgrade**.
3. You'll be redirected to a secure Stripe checkout.
4. After payment, you're back inside ArloSEO with the new plan live immediately.

## Managing your subscription

From **Settings → Billing → Manage subscription**, you can:

- Switch between monthly and annual (annual = 2 months free)
- Upgrade or downgrade between plans
- Update your card or billing email
- Download every invoice as PDF
- Cancel — you keep Pro access until the end of the current period

## Refunds

We offer a **no‑questions‑asked 14‑day refund** on any first‑time paid charge. Email \`billing@arloseo.com\` from the address on the account and we'll process it same business day.

## Tax & VAT

Invoices include local VAT / GST where required. You can add your business VAT ID in **Settings → Billing → Tax info** and Arlo will regenerate prior invoices with it on request.

## Common billing questions

**Will my data be deleted if I cancel?** No — your projects, audits, and drafts stay for 90 days after cancellation. You can re‑subscribe any time and pick up exactly where you left off.

**Can I pause my subscription?** Yes — downgrade to Free, then upgrade again whenever. Your project history persists.

**Do you offer non‑profit or student discounts?** Yes, 50% off Pro. Email \`hello@arloseo.com\` with proof of status.
`,
  },
];

export function getDocBySlug(slug) {
  return DOCS.find((d) => d.slug === slug) || null;
}

export function getDocsByCategory() {
  return DOC_CATEGORIES.map((c) => ({
    ...c,
    items: DOCS.filter((d) => d.category === c.id),
  }));
}
